// The login screen: shown instead of the app whenever the server answers 401 login_required (a new
// device, a session that ended, after signing out). A real form, so browsers and password
// managers recognise it: method="post", autocomplete="on", name/id/autocomplete on both fields, a
// submit button. The submit event is handled here (JSON to /api/login, never a form post), then
// state/actions.ts logIn() reloads the page, which is the navigation password managers wait for
// before offering to save. Phone first (390px): one column, 44px+ targets, no truncation.
import { useId, useRef, useState, type FormEvent } from 'react';
import { useT } from '../i18n';
import { ApiError } from '../lib/api';
import { logIn } from '../state/actions';
import { useApp } from '../state/store';
import { useDocumentPrefs } from './theme';

export function Login() {
  const t = useT();
  const L = t.login;
  const theme = useApp((s) => s.theme);
  const lang = useApp((s) => s.lang);
  // This device was signed in before (a cached identity): say that the session ended.
  const ended = useApp((s) => s.identity === 'cached');
  useDocumentPrefs(theme, lang);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userRef = useRef<HTMLInputElement>(null);
  const passRef = useRef<HTMLInputElement>(null);
  const errorId = useId();

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    const username = userRef.current?.value.trim() ?? '';
    const password = passRef.current?.value ?? '';
    if (!username || !password) {
      setError(L.empty);
      (username ? passRef : userRef).current?.focus();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await logIn(username, password);
    } catch (err) {
      const a = err instanceof ApiError ? err : null;
      if (a?.reason === 'bad_credentials') setError(L.wrong);
      else if (a?.reason === 'login_locked') setError(L.locked(Math.max(1, Math.ceil((a.retryAfter ?? 900) / 60))));
      else if (!a || a.status === 0) setError(L.offline);
      else setError(L.failed);
      setBusy(false);
      if (passRef.current) {
        passRef.current.value = '';
        passRef.current.focus();
      }
    }
  };

  return (
    <div className="login">
      <main className="login-main" id="main">
        <p className="mark hv login-mark">{t.appName}</p>
        <h1 className="page-h login-h">{L.title}</h1>
        {ended ? <p className="set-p login-ended">{L.ended}</p> : null}
        <form className="login-form" method="post" autoComplete="on" noValidate onSubmit={(e) => void submit(e)} aria-describedby={error ? errorId : undefined}>
          <div className="field-row">
            <label className="field-label" htmlFor="username">
              {L.username}
            </label>
            <input
              ref={userRef}
              className="field"
              type="text"
              name="username"
              id="username"
              autoComplete="username"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              inputMode="text"
              enterKeyHint="next"
              required
              readOnly={busy}
              aria-invalid={error === L.wrong || undefined}
            />
          </div>
          <div className="field-row">
            <label className="field-label" htmlFor="password">
              {L.password}
            </label>
            <input
              ref={passRef}
              className="field"
              type="password"
              name="password"
              id="password"
              autoComplete="current-password"
              enterKeyHint="go"
              required
              readOnly={busy}
              aria-invalid={error === L.wrong || undefined}
            />
          </div>
          <p id={errorId} className="field-error login-error" role="alert" aria-live="assertive">
            {error ?? ''}
          </p>
          <button type="submit" className="btn btn-primary btn-big" disabled={busy}>
            {busy ? L.submitting : L.submit}
          </button>
        </form>
      </main>
    </div>
  );
}
